import { Heart } from "lucide-react";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@/lib/router-compat";

interface Props {
  stockNumber: string;
  className?: string;
}

export default function WishlistButton({ stockNumber, className }: Props) {
  const { user } = useCustomerAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: isSaved } = useQuery({
    queryKey: ["wishlist-check", user?.id, stockNumber],
    enabled: !!user,
    queryFn: async () => {
      const { count } = await supabase
        .from("wishlists")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user!.id)
        .eq("stock_number", stockNumber);
      return (count || 0) > 0;
    },
    staleTime: 60_000,
  });

  const toggle = useMutation({
    mutationFn: async () => {
      if (isSaved) {
        await supabase.from("wishlists").delete().eq("user_id", user!.id).eq("stock_number", stockNumber);
      } else {
        await supabase.from("wishlists").insert({ user_id: user!.id, stock_number: stockNumber });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wishlist-check", user?.id, stockNumber] });
      qc.invalidateQueries({ queryKey: ["wishlist"] });
      toast({ title: isSaved ? "Removed from wishlist" : "Added to wishlist" });
    },
  });

  if (!user) {
    const handleGuest = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toast({ title: "Sign in to save", description: "Create a free account to save units to your wishlist." });
      const redirect = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
      navigate(`/community-login?mode=signup&redirect=${encodeURIComponent(redirect)}`);
    };
    return (
      <button
        onClick={handleGuest}
        className={cn(
          "rounded-full p-1.5 transition-colors bg-background/80 text-muted-foreground hover:text-primary",
          className
        )}
        aria-label="Save to wishlist"
      >
        <Heart className="h-4 w-4" />
      </button>
    );
  }

  return (
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle.mutate(); }}
      className={cn(
        "rounded-full p-1.5 transition-colors",
        isSaved ? "bg-primary/10 text-primary" : "bg-background/80 text-muted-foreground hover:text-primary",
        className
      )}
      aria-label={isSaved ? "Remove from wishlist" : "Add to wishlist"}
    >
      <Heart className={cn("h-4 w-4", isSaved && "fill-current")} />
    </button>
  );
}

/**
 * Full-width "Save to Wishlist" button for the unit detail page CTA grid.
 * - Logged-in: toggles wishlist entry.
 * - Logged-out: routes to login with redirect back to current page.
 */
export function SaveToWishlistButton({ stockNumber, className }: Props) {
  const { user } = useCustomerAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: isSaved } = useQuery({
    queryKey: ["wishlist-check", user?.id, stockNumber],
    enabled: !!user,
    queryFn: async () => {
      const { count } = await supabase
        .from("wishlists")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user!.id)
        .eq("stock_number", stockNumber);
      return (count || 0) > 0;
    },
    staleTime: 60_000,
  });

  const toggle = useMutation({
    mutationFn: async () => {
      if (isSaved) {
        await supabase.from("wishlists").delete().eq("user_id", user!.id).eq("stock_number", stockNumber);
      } else {
        await supabase.from("wishlists").insert({ user_id: user!.id, stock_number: stockNumber });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wishlist-check", user?.id, stockNumber] });
      qc.invalidateQueries({ queryKey: ["wishlist"] });
      qc.invalidateQueries({ queryKey: ["wishlist-count"] });
      toast({ title: isSaved ? "Removed from wishlist" : "Added to wishlist" });
    },
  });

  const handleClick = () => {
    if (!user) {
      toast({ title: "Sign in to save", description: "Create an account or log in to save units to your wishlist." });
      const redirect = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
      navigate(`/community-login?redirect=${encodeURIComponent(redirect)}`);
      return;
    }
    toggle.mutate();
  };

  return (
    <Button
      variant="outline"
      className={cn("justify-start gap-2 text-sm", className)}
      aria-label={isSaved ? "Remove from wishlist" : "Save to wishlist"}
      onClick={handleClick}
    >
      <Heart className={cn("h-4 w-4 text-primary", isSaved && "fill-current")} />
      {isSaved ? "Saved to Wishlist" : "Save to Wishlist"}
    </Button>
  );
}
